/**
 * Network Logger
 * Intercepta requests e loga metadados (sem PII)
 */

import { unifiedLogger } from './unified-logger';
import { sanitizeUrl } from './pii-redaction';

let isInitialized = false;

/**
 * Gera request ID único
 */
function generateRequestId(): string {
  return `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Inicializa interceptação de network requests
 */
export function initNetworkLogger() {
  if (isInitialized) return;
  
  const originalFetch = window.fetch;
  
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : String(input));
    const method = init?.method || 'GET';
    const requestId = generateRequestId();
    
    const startTime = Date.now();
    
    try {
      // Injetar headers de correlação
      const headers = new Headers(init?.headers);
      headers.set('X-Correlation-ID', unifiedLogger.getCorrelationId());
      headers.set('X-Request-ID', requestId);
      
      // Adicionar device info headers
      const { getDeviceInfo } = await import('./device-info');
      const deviceInfo = await getDeviceInfo();
      headers.set('X-Platform', deviceInfo.platform);
      headers.set('X-App-Version', deviceInfo.app_version);
      headers.set('X-Build-Number', deviceInfo.build_number);
      
      // Fazer request
      const response = await originalFetch(input, {
        ...init,
        headers
      });
      
      const duration = Date.now() - startTime;
      
      // Log apenas metadados (NUNCA corpo com PII)
      unifiedLogger.debug(
        'Network request completed',
        'network.request',
        {
          url: sanitizeUrl(url),
          method,
          status: response.status,
          duration_ms: duration,
          request_id: requestId,
          response_size: response.headers.get('content-length'),
          content_type: response.headers.get('content-type'),
        }
      );
      
      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      unifiedLogger.error(
        'Network request failed',
        'network.request',
        error,
        {
          url: sanitizeUrl(url),
          method,
          duration_ms: duration,
          request_id: requestId,
        }
      );
      
      throw error;
    }
  };
  
  isInitialized = true;
}

/**
 * Desabilita interceptação (útil para testes)
 */
export function disableNetworkLogger() {
  // Não podemos facilmente restaurar o fetch original, então apenas marcamos como desabilitado
  isInitialized = false;
}
